import React, { useState, Suspense } from 'react';
import { Box, Typography, Paper, Tabs, Tab, CircularProgress, Divider } from '@mui/material';
import { Hub, Speed, AttachMoney, AltRoute, ShowChart, Engineering, Public } from '@mui/icons-material';

// Lazy load the individual tab components
const CommandCenterTab = React.lazy(() => import('./analytics/CommandCenterTab'));
const FinancialsTab = React.lazy(() => import('./analytics/FinancialsTab'));
const GeoIntelligenceTab = React.lazy(() => import('./analytics/GeoIntelligenceTab'));
const FulfillmentTab = React.lazy(() => import('./analytics/FulfillmentTab'));
const GrowthTab = React.lazy(() => import('./analytics/GrowthTab'));
const FleetTab = React.lazy(() => import('./analytics/FleetTab'));

const COLORS = {
    bg: "#F1F5F9",
    surface: "#FFFFFF",
    header: "#0F172A",
    border: "#E2E8F0",
    indigo: "#6366f1"
};

const Analytics: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', bgcolor: COLORS.bg, pb: 4 }}>
            {/* Global Header */}
            <Paper elevation={0} sx={{
                p: 2, m: 2, borderRadius: 3, border: `1px solid ${COLORS.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: COLORS.surface
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: 3, bgcolor: COLORS.header,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Hub sx={{ color: COLORS.surface, fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1 }}>SWIFTAGO INTELLIGENCE</Typography>
                        <Typography variant="caption" fontWeight={700} color="textSecondary">
                            ENTERPRISE TELEMETRY & ANALYTICS
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Navigation Tabs */}
            <Box sx={{ px: 2, mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 48,
                        '& .MuiTab-root': { fontWeight: 700, fontSize: '0.85rem', minHeight: 48 },
                        '& .Mui-selected': { color: COLORS.indigo }
                    }}
                >
                    <Tab icon={<Speed fontSize="small" />} iconPosition="start" label="Command Center" />
                    <Tab icon={<AttachMoney fontSize="small" />} iconPosition="start" label="Financials" />
                    <Tab icon={<Public fontSize="small" />} iconPosition="start" label="Geo-Intelligence" />
                    <Tab icon={<AltRoute fontSize="small" />} iconPosition="start" label="Fulfillment" />
                    <Tab icon={<ShowChart fontSize="small" />} iconPosition="start" label="Growth & Subsidies" />
                    <Tab icon={<Engineering fontSize="small" />} iconPosition="start" label="Fleet Health" />
                </Tabs>
                <Divider />
            </Box>

            {/* Tab Content Area */}
            <Box sx={{ px: 2 }}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: COLORS.indigo }} />
                    </Box>
                }>
                    {/* LIVE POLLING TABS: Kept alive in background via display: none.
                        We pass isPageActive so Recharts knows when to unmount/remount safely.
                    */}

                    <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                        <CommandCenterTab isPageActive={activeTab === 0} />
                    </Box>

                    {/* HEAVY DB TABS: These can safely unmount entirely when inactive to save RAM */}
                    {activeTab === 1 && <FinancialsTab />}
                    {activeTab === 2 && <GeoIntelligenceTab />}
                    {activeTab === 3 && <FulfillmentTab />}
                    {activeTab === 4 && <GrowthTab />}
                    {activeTab === 5 && <FleetTab />}
                </Suspense>
            </Box>
        </Box>
    );
};

export default Analytics;